'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

export default function FinalCTA() {
  const sectionRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const tagRef = useRef<HTMLSpanElement>(null);
  const headLine1Ref = useRef<HTMLSpanElement>(null);
  const headLine2Ref = useRef<HTMLSpanElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const proofRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    let ctx: { revert: () => void } | undefined;

    const init = async () => {
      const { gsap } = await import('gsap');
      const { ScrollTrigger } = await import('gsap/ScrollTrigger');
      gsap.registerPlugin(ScrollTrigger);

      ctx = gsap.context(() => {
        // ── BACKGROUND GRADIENT SHIFT (scrub) ─────────────────────────
        gsap.fromTo(
          bgRef.current,
          { backgroundPosition: '0% 50%' },
          {
            backgroundPosition: '100% 50%',
            ease: 'none',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 2,
            },
          }
        );

        // ── AMBIENT GLOW PULSE ────────────────────────────────────────
        gsap.to(glowRef.current, {
          opacity: 0.25,
          scale: 1.15,
          duration: 3,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
        });

        // ── CONTENT TIMELINE (scrub) ──────────────────────────────────
        const contentTl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 65%',
            end: 'top 20%',
            scrub: 1,
          },
        });

        contentTl
          .fromTo(tagRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.3 }, 0)
          .fromTo(
            headLine1Ref.current,
            { y: '100%' },
            { y: '0%', duration: 0.4 },
            0.1
          )
          .fromTo(
            headLine2Ref.current,
            { y: '100%' },
            { y: '0%', duration: 0.4 },
            0.2
          )
          .fromTo(
            subRef.current,
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.35 },
            0.35
          )
          .fromTo(
            ctaRef.current,
            { opacity: 0, y: 24, scale: 0.95 },
            { opacity: 1, y: 0, scale: 1, duration: 0.35 },
            0.5
          )
          .fromTo(
            proofRef.current,
            { opacity: 0 },
            { opacity: 1, duration: 0.3 },
            0.65
          );
      }, sectionRef);
    };

    init();
    return () => ctx?.revert();
  }, []);

  return (
    <section data-component-id="src/app/components/FinalCTA.tsx:102:4" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="102" data-component-end-line="199" data-component-file="FinalCTA.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-32%20md%3Apy-40%20bg-background%22%7D"
      ref={sectionRef}
      className="py-32 md:py-40 bg-background"
      aria-label="Call to action"
    >
      <div data-component-id="src/app/components/FinalCTA.tsx:107:6" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="107" data-component-end-line="198" data-component-file="FinalCTA.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%22%7D" className="max-w-7xl mx-auto px-6">
        <div data-component-id="src/app/components/FinalCTA.tsx:108:8" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="108" data-component-end-line="197" data-component-file="FinalCTA.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20rounded-3xl%20overflow-hidden%22%7D" className="relative rounded-3xl overflow-hidden" style={{ minHeight: '480px' }}>
          {/* Animated gradient background */}
          <div data-component-id="src/app/components/FinalCTA.tsx:110:10" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="110" data-component-end-line="119" data-component-file="FinalCTA.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%22%7D"
            ref={bgRef}
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(135deg, #0A0A0A 0%, #1a1a1a 25%, #2d1a12 50%, #1a0a0a 75%, #0A0A0A 100%)',
              backgroundSize: '300% 300%',
              backgroundPosition: '0% 50%',
            }}
          />

          {/* Ambient glow */}
          <div data-component-id="src/app/components/FinalCTA.tsx:122:10" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="122" data-component-end-line="132" data-component-file="FinalCTA.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20pointer-events-none%22%7D"
            ref={glowRef}
            className="absolute pointer-events-none"
            style={{
              inset: '-20%',
              background:
                'radial-gradient(ellipse 50% 40% at 50% 85%, rgba(255,106,61,0.18) 0%, transparent 65%)',
              opacity: 0.12,
            }}
            aria-hidden="true"
          />

          {/* Content */}
          <div data-component-id="src/app/components/FinalCTA.tsx:135:10" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="135" data-component-end-line="196" data-component-file="FinalCTA.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20z-10%20flex%20flex-col%20items-center%20justify-center%20text-center%20px-8%20py-24%22%7D" className="relative z-10 flex flex-col items-center justify-center text-center px-8 py-24">
            <span data-component-id="src/app/components/FinalCTA.tsx:136:12" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="136" data-component-end-line="142" data-component-file="FinalCTA.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22section-tag%20text-white%2F35%20block%20mb-8%22%2C%22textContent%22%3A%22The%20Trail%20is%20Waiting%22%7D"
              ref={tagRef}
              className="section-tag text-white/35 block mb-8"
              style={{ opacity: 0 }}
            >
              The Trail is Waiting
            </span>

            <h2 data-component-id="src/app/components/FinalCTA.tsx:144:12" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="144" data-component-end-line="155" data-component-file="FinalCTA.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-white%20font-bold%20tracking-tightest%20text-4xl%20md%3Atext-6xl%20lg%3Atext-7xl%20leading-none%20mb-6%22%7D" className="text-white font-bold tracking-tightest text-4xl md:text-6xl lg:text-7xl leading-none mb-6">
              <span data-component-id="src/app/components/FinalCTA.tsx:145:14" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="145" data-component-end-line="149" data-component-file="FinalCTA.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22hero-line%20block%22%7D" className="hero-line block">
                <span data-component-id="src/app/components/FinalCTA.tsx:146:16" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="146" data-component-end-line="148" data-component-file="FinalCTA.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22hero-line-inner%22%2C%22textContent%22%3A%22Your%20Next%20Story%22%7D" ref={headLine1Ref} className="hero-line-inner">
                  Your Next Story
                </span>
              </span>
              <span data-component-id="src/app/components/FinalCTA.tsx:150:14" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="150" data-component-end-line="154" data-component-file="FinalCTA.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22hero-line%20block%22%7D" className="hero-line block">
                <span data-component-id="src/app/components/FinalCTA.tsx:151:16" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="151" data-component-end-line="153" data-component-file="FinalCTA.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22hero-line-inner%22%2C%22textContent%22%3A%22Starts%20Here.%22%7D" ref={headLine2Ref} className="hero-line-inner" style={{ color: 'var(--accent)' }}>
                  Starts Here.
                </span>
              </span>
            </h2>

            <p data-component-id="src/app/components/FinalCTA.tsx:157:12" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="157" data-component-end-line="165" data-component-file="FinalCTA.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-white%2F55%20text-lg%20md%3Atext-xl%20max-w-lg%20leading-relaxed%20mb-12%22%2C%22textContent%22%3A%22Not%20someday.%20Not%20later.%20The%20mountains%20are%20calling.%22%7D"
              ref={subRef}
              className="text-white/55 text-lg md:text-xl max-w-lg leading-relaxed mb-12"
              style={{ opacity: 0 }}
            >
              Not someday. Not later.
              <br data-component-id="src/app/components/FinalCTA.tsx:163:14" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="163" data-component-end-line="163" data-component-file="FinalCTA.tsx" data-component-name="br" data-component-content="%7B%22elementName%22%3A%22br%22%7D" />
              The mountains are calling.
            </p>

            <div data-component-id="src/app/components/FinalCTA.tsx:167:12" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="167" data-component-end-line="187" data-component-file="FinalCTA.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20items-center%20gap-4%22%7D"
              ref={ctaRef}
              className="flex flex-col sm:flex-row items-center gap-4"
              style={{ opacity: 0 }}
            >
              <Link data-component-id="src/app/components/FinalCTA.tsx:172:14" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="172" data-component-end-line="180" data-component-file="FinalCTA.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%23featured-treks%22%2C%22className%22%3A%22pulse-ring%20btn-accent%20inline-flex%20items-center%20gap-2%20px-8%20py-4%20rounded-full%20text-sm%20font-bold%20min-w-%5B200px%5D%20justify-center%22%2C%22textContent%22%3A%22Explore%20Treks%22%7D"
                href="#featured-treks"
                className="pulse-ring btn-accent inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm font-bold min-w-[200px] justify-center"
              >
                Explore Treks
                <svg data-component-id="src/app/components/FinalCTA.tsx:177:16" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="177" data-component-end-line="179" data-component-file="FinalCTA.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path data-component-id="src/app/components/FinalCTA.tsx:178:18" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="178" data-component-end-line="178" data-component-file="FinalCTA.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
              <Link data-component-id="src/app/components/FinalCTA.tsx:181:14" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="181" data-component-end-line="186" data-component-file="FinalCTA.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%23%22%2C%22className%22%3A%22inline-flex%20items-center%20gap-2%20px-8%20py-4%20rounded-full%20text-sm%20font-semibold%20text-white%20border%20border-white%2F20%20hover%3Aborder-white%2F60%20transition-colors%20min-w-%5B200px%5D%20justify-center%22%2C%22textContent%22%3A%22Talk%20to%20an%20Expert%22%7D"
                href="#"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm font-semibold text-white border border-white/20 hover:border-white/60 transition-colors min-w-[200px] justify-center"
              >
                Talk to an Expert
              </Link>
            </div>

            <p data-component-id="src/app/components/FinalCTA.tsx:189:12" data-component-path="src/app/components/FinalCTA.tsx" data-component-line="189" data-component-end-line="195" data-component-file="FinalCTA.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-white%2F25%20text-xs%20mt-10%20tracking-wide%22%2C%22textContent%22%3A%22Trusted%20by%203%2C200%2B%20trekkers%20from%20across%20India%20%C2%B7%20Average%20rating%204.9%E2%98%85%22%7D"
              ref={proofRef}
              className="text-white/25 text-xs mt-10 tracking-wide"
              style={{ opacity: 0 }}
            >
              Trusted by 3,200+ trekkers from across India · Average rating 4.9★
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}